import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Pagination,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  SwapHoriz as SwapIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import matchingService, {
  Invoice,
  BankTransaction,
  Match,
} from "../services/matchingService";
import InvoiceCard from "./InvoiceCard";
import TransactionCard from "./TransactionCard";
import MatchCard from "./MatchCard";

interface DragData {
  type: "invoice" | "transaction";
  item: Invoice | BankTransaction;
}

interface MatchPreview {
  invoice: Invoice;
  transaction: BankTransaction;
  score: number;
  criteria: any;
}

const DragDropWorkspace: React.FC = () => {
  // Data states
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [transactions, setTransactions] = useState<BankTransaction[]>([]);
  const [recentMatches, setRecentMatches] = useState<Match[]>([]);

  // Pagination states
  const [invoicePage, setInvoicePage] = useState(1);
  const [transactionPage, setTransactionPage] = useState(1);
  const [invoiceTotalPages, setInvoiceTotalPages] = useState(1);
  const [transactionTotalPages, setTransactionTotalPages] = useState(1);

  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Drag & Drop states
  const [draggedItem, setDraggedItem] = useState<DragData | null>(null);
  const [dropTarget, setDropTarget] = useState<
    "invoice" | "transaction" | null
  >(null);

  // Match creation states
  const [matchPreview, setMatchPreview] = useState<MatchPreview | null>(null);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const [matchNotes, setMatchNotes] = useState("");
  const [matchAmount, setMatchAmount] = useState<string>("");
  const [creating, setCreating] = useState(false);

  // Selected items for suggestions
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [selectedTransaction, setSelectedTransaction] =
    useState<BankTransaction | null>(null);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const itemsPerPage = 10;

  useEffect(() => {
    loadData();
  }, [invoicePage, transactionPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [invoicesRes, transactionsRes, matchesRes] = await Promise.all([
        matchingService.getUnmatchedInvoices({
          page: invoicePage,
          limit: itemsPerPage,
        }),
        matchingService.getUnmatchedTransactions({
          page: transactionPage,
          limit: itemsPerPage,
          days: 90,
        }),
        matchingService.listMatches({
          status: "pending",
          page: 1,
          limit: 5,
        }),
      ]);

      setInvoices(invoicesRes.invoices || []);
      setInvoiceTotalPages(invoicesRes.pagination.pages);

      setTransactions(transactionsRes.transactions || []);
      setTransactionTotalPages(transactionsRes.pagination.pages);

      setRecentMatches(matchesRes.matches || []);
    } catch (err: any) {
      setError(err.message || "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (data: DragData) => {
    setDraggedItem(data);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDropTarget(null);
  };

  const handleDragOver = (
    e: React.DragEvent,
    targetType: "invoice" | "transaction"
  ) => {
    e.preventDefault();
    if (draggedItem && draggedItem.type !== targetType) {
      setDropTarget(targetType);
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (
    e: React.DragEvent,
    targetType: "invoice" | "transaction",
    targetItem: Invoice | BankTransaction
  ) => {
    e.preventDefault();
    setDropTarget(null);

    if (!draggedItem || draggedItem.type === targetType) {
      handleDragEnd();
      return;
    }

    // Prepare match preview
    let invoice: Invoice;
    let transaction: BankTransaction;

    if (draggedItem.type === "invoice") {
      invoice = draggedItem.item as Invoice;
      transaction = targetItem as BankTransaction;
    } else {
      invoice = targetItem as Invoice;
      transaction = draggedItem.item as BankTransaction;
    }

    // Get suggestions to calculate score
    try {
      const result = await matchingService.getSuggestionsForInvoice(
        invoice.id,
        {
          limit: 1,
          threshold: 0,
        }
      );

      const suggestion = result.suggestions.find(
        (s) => s.transaction?.id === transaction.id
      );

      setMatchPreview({
        invoice,
        transaction,
        score: suggestion?.score || 0,
        criteria: suggestion?.criteria || {},
      });

      // Set default amount to transaction value (absolute)
      const transactionAmount = Math.abs(Number(transaction.valor));
      const invoiceAmount = Number(invoice.valor);
      setMatchAmount(Math.min(transactionAmount, invoiceAmount).toFixed(2));

      setMatchDialogOpen(true);
    } catch (err) {
      console.error("Error calculating match score:", err);
      // Even if score calculation fails, allow manual match
      setMatchPreview({
        invoice,
        transaction,
        score: 0,
        criteria: {},
      });

      // Set default amount even on error
      const transactionAmount = Math.abs(Number(transaction.valor));
      const invoiceAmount = Number(invoice.valor);
      setMatchAmount(Math.min(transactionAmount, invoiceAmount).toFixed(2));

      setMatchDialogOpen(true);
    }

    handleDragEnd();
  };

  const handleCreateMatch = async () => {
    if (!matchPreview) return;

    try {
      setCreating(true);
      setError(null);

      const amountToMatch = parseFloat(matchAmount);
      if (isNaN(amountToMatch) || amountToMatch <= 0) {
        setError("Valor inválido. Insira um valor maior que 0.");
        setCreating(false);
        return;
      }

      await matchingService.createMatch({
        invoice_id: matchPreview.invoice.id,
        transaction_id: matchPreview.transaction.id,
        amount_matched: amountToMatch,
        notes: matchNotes,
        matched_by: "user",
      });

      setSuccess("Match criado com sucesso!");
      setMatchDialogOpen(false);
      setMatchNotes("");
      setMatchAmount("");
      setMatchPreview(null);

      // Reload data
      await loadData();
    } catch (err: any) {
      setError(err.message || "Erro ao criar match");
    } finally {
      setCreating(false);
    }
  };

  const handleInvoiceClick = async (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setSelectedTransaction(null);
    setLoadingSuggestions(true);

    try {
      const result = await matchingService.getSuggestionsForInvoice(
        invoice.id,
        {
          limit: 5,
          threshold: 50,
        }
      );
      setSuggestions(result.suggestions);
    } catch (err) {
      console.error("Error loading suggestions:", err);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleTransactionClick = async (transaction: BankTransaction) => {
    setSelectedTransaction(transaction);
    setSelectedInvoice(null);
    setLoadingSuggestions(true);

    try {
      const result = await matchingService.getSuggestionsForTransaction(
        transaction.id,
        {
          limit: 5,
          threshold: 50,
        }
      );
      setSuggestions(result.suggestions);
    } catch (err) {
      console.error("Error loading suggestions:", err);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: any) => {
    if (selectedInvoice && suggestion.transaction) {
      setMatchPreview({
        invoice: selectedInvoice,
        transaction: suggestion.transaction,
        score: suggestion.score,
        criteria: suggestion.criteria,
      });

      // Set default amount
      const transactionAmount = Math.abs(Number(suggestion.transaction.valor));
      const invoiceAmount = Number(selectedInvoice.valor);
      setMatchAmount(Math.min(transactionAmount, invoiceAmount).toFixed(2));

      setMatchDialogOpen(true);
    } else if (selectedTransaction && suggestion.invoice) {
      setMatchPreview({
        invoice: suggestion.invoice,
        transaction: selectedTransaction,
        score: suggestion.score,
        criteria: suggestion.criteria,
      });

      // Set default amount
      const transactionAmount = Math.abs(Number(selectedTransaction.valor));
      const invoiceAmount = Number(suggestion.invoice.valor);
      setMatchAmount(Math.min(transactionAmount, invoiceAmount).toFixed(2));

      setMatchDialogOpen(true);
    }
  };

  const handleApproveMatch = async (matchId: number) => {
    try {
      await matchingService.approveMatch(matchId);
      setSuccess("Match aprovado!");
      await loadData();
    } catch (err: any) {
      setError(err.message || "Erro ao aprovar match");
    }
  };

  const handleRejectMatch = async (matchId: number) => {
    try {
      await matchingService.rejectMatch(matchId);
      setSuccess("Match rejeitado!");
      await loadData();
    } catch (err: any) {
      setError(err.message || "Erro ao rejeitar match");
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Matching Manual
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Atualizar
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Instructions */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Como usar:</strong> Arraste uma fatura para uma transação (ou
          vice-versa) para criar um match manual. Clique num item para ver
          sugestões automáticas.
        </Typography>
      </Alert>

      {/* Main Workspace - 3 Columns */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
          gap: 2,
        }}
      >
        {/* Column 1: Unmatched Invoices */}
        <Box>
          <Paper sx={{ p: 2, height: "calc(100vh - 350px)", overflow: "auto" }}>
            <Typography variant="h6" gutterBottom>
              Faturas Não Matchadas ({invoices.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              onDragOver={(e) => handleDragOver(e, "invoice")}
              onDragLeave={handleDragLeave}
              sx={{
                minHeight: "400px",
                bgcolor:
                  dropTarget === "invoice" ? "action.hover" : "transparent",
                borderRadius: 1,
                transition: "background-color 0.2s",
              }}
            >
              {invoices.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  Nenhuma fatura disponível
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {invoices.map((invoice) => (
                    <Box
                      key={invoice.id}
                      onDrop={(e) => handleDrop(e, "invoice", invoice)}
                    >
                      <InvoiceCard
                        invoice={invoice}
                        draggable
                        onDragStart={(e) =>
                          handleDragStart({ type: "invoice", item: invoice })
                        }
                        selected={selectedInvoice?.id === invoice.id}
                        onClick={() => handleInvoiceClick(invoice)}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {invoiceTotalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={invoiceTotalPages}
                  page={invoicePage}
                  onChange={(_, value) => setInvoicePage(value)}
                  size="small"
                />
              </Box>
            )}
          </Paper>
        </Box>

        {/* Column 2: Recent Matches or Suggestions */}
        <Box>
          <Paper sx={{ p: 2, height: "calc(100vh - 350px)", overflow: "auto" }}>
            {selectedInvoice || selectedTransaction ? (
              <>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">Sugestões</Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSelectedInvoice(null);
                      setSelectedTransaction(null);
                      setSuggestions([]);
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Divider sx={{ mb: 2 }} />

                {loadingSuggestions ? (
                  <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </Box>
                ) : suggestions.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    Nenhuma sugestão encontrada (score &gt; 50%)
                  </Typography>
                ) : (
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {suggestions.map((suggestion, idx) => (
                      <Box
                        key={idx}
                        onClick={() => handleSuggestionClick(suggestion)}
                        sx={{ cursor: "pointer" }}
                      >
                        {selectedInvoice ? (
                          <TransactionCard
                            transaction={suggestion.transaction}
                            selected={false}
                          />
                        ) : (
                          <InvoiceCard
                            invoice={suggestion.invoice}
                            selected={false}
                          />
                        )}
                        <Chip
                          label={`Score: ${suggestion.score}%`}
                          color={
                            suggestion.score >= 70
                              ? "success"
                              : suggestion.score >= 50
                              ? "warning"
                              : "default"
                          }
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            ) : (
              <>
                <Typography variant="h6" gutterBottom>
                  Matches Pendentes ({recentMatches.length})
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {recentMatches.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    Nenhum match pendente
                  </Typography>
                ) : (
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {recentMatches.map((match) => (
                      <MatchCard
                        key={match.id}
                        match={match}
                        onApprove={handleApproveMatch}
                        onReject={handleRejectMatch}
                      />
                    ))}
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>

        {/* Column 3: Unmatched Transactions */}
        <Box>
          <Paper sx={{ p: 2, height: "calc(100vh - 350px)", overflow: "auto" }}>
            <Typography variant="h6" gutterBottom>
              Movimentos Não Matchados ({transactions.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              onDragOver={(e) => handleDragOver(e, "transaction")}
              onDragLeave={handleDragLeave}
              sx={{
                minHeight: "400px",
                bgcolor:
                  dropTarget === "transaction" ? "action.hover" : "transparent",
                borderRadius: 1,
                transition: "background-color 0.2s",
              }}
            >
              {transactions.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  Nenhum movimento disponível
                </Typography>
              ) : (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  {transactions.map((transaction) => (
                    <Box
                      key={transaction.id}
                      onDrop={(e) => handleDrop(e, "transaction", transaction)}
                    >
                      <TransactionCard
                        transaction={transaction}
                        draggable
                        onDragStart={(e) =>
                          handleDragStart({
                            type: "transaction",
                            item: transaction,
                          })
                        }
                        selected={selectedTransaction?.id === transaction.id}
                        onClick={() => handleTransactionClick(transaction)}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {transactionTotalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={transactionTotalPages}
                  page={transactionPage}
                  onChange={(_, value) => setTransactionPage(value)}
                  size="small"
                />
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Match Confirmation Dialog */}
      <Dialog
        open={matchDialogOpen}
        onClose={() => !creating && setMatchDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SwapIcon color="primary" />
            Confirmar Match Manual
          </Box>
        </DialogTitle>
        <DialogContent>
          {matchPreview && (
            <Box>
              {/* Score */}
              <Box display="flex" justifyContent="center" mb={3}>
                <Chip
                  label={`Score Calculado: ${matchPreview.score}%`}
                  color={
                    matchPreview.score >= 70
                      ? "success"
                      : matchPreview.score >= 50
                      ? "warning"
                      : "error"
                  }
                  sx={{ fontSize: "1.1rem", py: 2.5 }}
                />
              </Box>

              {/* Invoice Details */}
              <Typography variant="subtitle2" gutterBottom>
                Fatura:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2">
                  <strong>{matchPreview.invoice.numero_fatura}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Valor: €{Number(matchPreview.invoice.valor).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data: {matchPreview.invoice.data_emissao}
                </Typography>
                {matchPreview.invoice.nif_emitente && (
                  <Typography variant="body2" color="text.secondary">
                    NIF: {matchPreview.invoice.nif_emitente}
                  </Typography>
                )}
              </Paper>

              {/* Transaction Details */}
              <Typography variant="subtitle2" gutterBottom>
                Movimento Bancário:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="body2">
                  <strong>
                    €
                    {Math.abs(Number(matchPreview.transaction.valor)).toFixed(
                      2
                    )}
                  </strong>{" "}
                  (
                  {Number(matchPreview.transaction.valor) < 0
                    ? "Débito"
                    : "Crédito"}
                  )
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data: {matchPreview.transaction.data_movimento}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                  noWrap
                  title={matchPreview.transaction.descricao}
                >
                  {matchPreview.transaction.descricao}
                </Typography>
              </Paper>

              {/* Criteria */}
              {matchPreview.criteria &&
                Object.keys(matchPreview.criteria).length > 0 && (
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      Critérios de Match:
                    </Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {Object.entries(matchPreview.criteria).map(
                        ([key, value]) => (
                          <Chip
                            key={key}
                            label={`${key}: ${value}`}
                            size="small"
                            variant="outlined"
                          />
                        )
                      )}
                    </Box>
                  </Box>
                )}

              {/* Amount Matched */}
              <Box mb={2}>
                <TextField
                  fullWidth
                  type="number"
                  label="Valor a Associar"
                  value={matchAmount}
                  onChange={(e) => setMatchAmount(e.target.value)}
                  inputProps={{
                    min: 0,
                    step: 0.01,
                    max: Math.min(
                      Math.abs(Number(matchPreview.transaction.valor)),
                      Number(matchPreview.invoice.valor)
                    ),
                  }}
                  helperText={
                    <Box
                      component="span"
                      display="flex"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <span>
                        Transação: €
                        {Math.abs(
                          Number(matchPreview.transaction.valor)
                        ).toFixed(2)}
                      </span>
                      <span>
                        Fatura: €{Number(matchPreview.invoice.valor).toFixed(2)}
                      </span>
                    </Box>
                  }
                  required
                />
              </Box>

              {/* Notes */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notas (opcional)"
                value={matchNotes}
                onChange={(e) => setMatchNotes(e.target.value)}
                placeholder="Adicione observações sobre este match..."
              />

              {/* Warning for low score */}
              {matchPreview.score < 50 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <InfoIcon
                      fontSize="small"
                      sx={{ mr: 0.5, verticalAlign: "middle" }}
                    />
                    Score baixo. Certifique-se que este match está correto.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMatchDialogOpen(false);
              setMatchAmount("");
              setMatchNotes("");
            }}
            disabled={creating}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreateMatch}
            variant="contained"
            disabled={creating || !matchAmount || parseFloat(matchAmount) <= 0}
            color="primary"
          >
            {creating ? "A criar..." : "Confirmar Match"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default DragDropWorkspace;
